import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useEffect, useRef, useState } from "react"
import type { TurnPlan } from "../../app/gameLogic"

// A mentor must enter this code to resume play once a meter hits zero.
// Deliberately different from the app password so players who know the
// app password can't clear the gate themselves.
// TODO: Move this to an environment variable (e.g. VITE_APP_MENTOR_CODE).
const MENTOR_CODE = "greenlight"

/**
 * The single mentor-intervention dialog for the slide viewer. It is only
 * mounted while a gate is pending, so it costs nothing on ordinary turns.
 */
export const MentorGateModal: React.FC<{
  plan: TurnPlan | null
  onApprove: () => void
}> = ({ plan, onApprove }) => {
  const [code, setCode] = useState("")
  const [codeError, setCodeError] = useState(false)
  // Guards against double-submit while the modal's exit animation keeps its
  // old render interactive: the ref outlives those frozen closures.
  const approvalInFlight = useRef(false)

  useEffect(() => {
    approvalInFlight.current = false
    setCode("")
    setCodeError(false)
  }, [plan])

  const submit = () => {
    if (code.trim().toLowerCase() !== MENTOR_CODE) {
      setCodeError(true)
      return
    }
    if (approvalInFlight.current) return
    approvalInFlight.current = true
    onApprove()
  }

  return (
    <Modal
      isOpen={plan !== null}
      onClose={() => {}}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
    >
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
      <ModalContent>
        <ModalHeader>Mentor Intervention Required</ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="warning" variant="left-accent">
              <AlertIcon />
              <Box>
                <AlertTitle>Critical Level Reached</AlertTitle>
                <AlertDescription>
                  The following meter(s) have reached zero:
                  <Text fontWeight="bold" mt={2}>
                    {plan?.gatedMeters.join(", ")}
                  </Text>
                </AlertDescription>
              </Box>
            </Alert>

            <Text>
              A mentor must enter the code to continue the game and discuss this
              situation with the student.
            </Text>

            <Input
              placeholder="Enter mentor code"
              value={code}
              onChange={e => {
                setCode(e.target.value)
                setCodeError(false)
              }}
              onKeyDown={e => {
                if (e.key === "Enter") submit()
              }}
              isInvalid={codeError}
              errorBorderColor="red.500"
              size="lg"
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={submit} w="100%">
            Continue Game
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
